import { BlogPost } from '../types/blog';
import { CreditTransaction } from '../types';

const CACHE_PREFIX = 'app_cache_';
const BLOG_CACHE_PREFIX = `${CACHE_PREFIX}blog_`;
const BLOG_POST_CACHE_PREFIX = `${CACHE_PREFIX}blog_post_`;
const CREDIT_HISTORY_CACHE_PREFIX = `${CACHE_PREFIX}credit_history_`;
const CREDIT_BALANCE_CACHE_PREFIX = `${CACHE_PREFIX}credit_balance_`;
const FEATURE_CACHE_PREFIX = `${CACHE_PREFIX}feature_`;
const FEATURE_CREDIT_COST_CACHE_PREFIX = `${CACHE_PREFIX}feature_credit_cost_`;
const FAVORITES_CACHE_PREFIX = `${CACHE_PREFIX}favorites_`;
const MY_DRAFTS_CACHE_PREFIX = `${CACHE_PREFIX}my_drafts_`;
const SYSTEM_SETTINGS_CACHE_PREFIX = `${CACHE_PREFIX}system_settings_`;

const BLOG_CACHE_EXPIRY = 60 * 60 * 1000; // 60 minutes for blog
const BLOG_POST_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours for individual posts
const CREDIT_HISTORY_CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes for credit history
const CREDIT_BALANCE_CACHE_EXPIRY = 10 * 60 * 1000; // 10 minutes for credit balance
const FEATURE_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours for feature data
const FEATURE_CREDIT_COST_CACHE_EXPIRY = 4 * 60 * 60 * 1000; // 4 hours for feature credit cost
const FAVORITES_CACHE_EXPIRY = 60 * 60 * 1000; // 60 minutes for favorites
const MY_DRAFTS_CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes for my drafts
const SYSTEM_SETTINGS_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours for system settings

interface CacheItem<T> {
  data: T;
  timestamp: number;
  version: string;
}

interface CreditHistoryCache {
  transactions: CreditTransaction[];
  totalCount: number;
  page: number;
}

interface FeatureData {
  id: string;
  name: string;
  slug: string;
  description?: string;
  credit_cost: number;
  group: string;
  is_active: boolean;
}

interface FavoritesCache {
  favorites: {
    id: string;
    name: string;
    slug: string;
    description?: string;
    group: string;
  }[];
  timestamp: number;
}

interface MyDraftsCache {
  files: any[];
  count: number;
  timestamp: number;
}

interface SystemSettingsCache {
  value: string;
  timestamp: number;
}

// Cache version - increment when data structure changes
const CACHE_VERSION = '1.0.0';

function getCacheKey(prefix: string, key: string): string {
  return `${prefix}${key}`;
}

function validateCache<T>(cached: string | null): CacheItem<T> | null {
  if (!cached) return null;

  try {
    const cacheItem: CacheItem<T> = JSON.parse(cached);
    
    if (cacheItem.version !== CACHE_VERSION) {
      return null;
    }

    if (!cacheItem.timestamp || typeof cacheItem.timestamp !== 'number') {
      return null;
    }

    return cacheItem;
  } catch {
    return null;
  }
}

function setCache<T>(prefix: string, key: string, data: T, expiry: number): void {
  try {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      version: CACHE_VERSION
    };
    localStorage.setItem(getCacheKey(prefix, key), JSON.stringify(cacheItem));
  } catch (error) {
    console.error('Cache write failed:', error);
    clearCache(prefix);
  }
}

function getCache<T>(prefix: string, key: string, expiry: number): T | null {
  try {
    const cached = localStorage.getItem(getCacheKey(prefix, key));
    const cacheItem = validateCache<T>(cached);

    if (!cacheItem) {
      localStorage.removeItem(getCacheKey(prefix, key));
      return null;
    }

    const isExpired = Date.now() - cacheItem.timestamp > expiry;
    if (isExpired) {
      localStorage.removeItem(getCacheKey(prefix, key));
      return null;
    }

    return cacheItem.data;
  } catch (error) {
    console.error('Cache read failed:', error);
    clearCache(prefix);
    return null;
  }
}

// System Settings Cache Functions
export function getSystemSettingCache(key: string): string | null {
  const cached = getCache<SystemSettingsCache>(SYSTEM_SETTINGS_CACHE_PREFIX, key, SYSTEM_SETTINGS_CACHE_EXPIRY);
  return cached?.value || null;
}

export function setSystemSettingCache(key: string, value: string): void {
  setCache<SystemSettingsCache>(
    SYSTEM_SETTINGS_CACHE_PREFIX,
    key,
    { value, timestamp: Date.now() },
    SYSTEM_SETTINGS_CACHE_EXPIRY
  );
}

export function clearSystemSettingCache(key: string): void {
  localStorage.removeItem(getCacheKey(SYSTEM_SETTINGS_CACHE_PREFIX, key));
}

// Blog Post Cache Functions
export function getBlogPostCache(slug: string): BlogPost | null {
  return getCache(BLOG_POST_CACHE_PREFIX, slug, BLOG_POST_CACHE_EXPIRY);
}

export function setBlogPostCache(slug: string, post: BlogPost): void {
  setCache(BLOG_POST_CACHE_PREFIX, slug, post, BLOG_POST_CACHE_EXPIRY);
}

function clearBlogPostCache(slug: string): void {
  localStorage.removeItem(getCacheKey(BLOG_POST_CACHE_PREFIX, slug));
}

// Credit Balance Cache Functions
export function setCreditBalanceCache(userId: string, credits: number): void {
  setCache(CREDIT_BALANCE_CACHE_PREFIX, userId, credits, CREDIT_BALANCE_CACHE_EXPIRY);
}

export function getCreditBalanceCache(userId: string): number | null {
  return getCache(CREDIT_BALANCE_CACHE_PREFIX, userId, CREDIT_BALANCE_CACHE_EXPIRY);
}

function clearCreditBalanceCache(userId: string): void {
  clearCache(`${CREDIT_BALANCE_CACHE_PREFIX}${userId}`);
}

// Credit History Cache Functions
export function setCreditHistoryCache(
  userId: string,
  page: number,
  data: CreditHistoryCache
): void {
  setCache(CREDIT_HISTORY_CACHE_PREFIX, `${userId}_${page}`, data, CREDIT_HISTORY_CACHE_EXPIRY);
}

export function getCreditHistoryCache(
  userId: string,
  page: number
): CreditHistoryCache | null {
  return getCache(CREDIT_HISTORY_CACHE_PREFIX, `${userId}_${page}`, CREDIT_HISTORY_CACHE_EXPIRY);
}

export function clearCreditHistoryCache(userId: string): void {
  clearCache(`${CREDIT_HISTORY_CACHE_PREFIX}${userId}`);
}

// Blog Cache Functions
export function getBlogCache(): BlogPost[] | null {
  return getCache(BLOG_CACHE_PREFIX, 'posts', BLOG_CACHE_EXPIRY);
}

export function setBlogCache(posts: BlogPost[]): void {
  setCache(BLOG_CACHE_PREFIX, 'posts', posts, BLOG_CACHE_EXPIRY);
}

export function getLatestBlogCache(): BlogPost[] | null {
  return getCache(BLOG_CACHE_PREFIX, 'latest_posts', BLOG_CACHE_EXPIRY);
}

export function setLatestBlogCache(posts: BlogPost[]): void {
  setCache(BLOG_CACHE_PREFIX, 'latest_posts', posts, BLOG_CACHE_EXPIRY);
}

// Feature Cache Functions
function getFeatureCache(slug: string): FeatureData | null {
  return getCache(FEATURE_CACHE_PREFIX, slug, FEATURE_CACHE_EXPIRY);
}

function setFeatureCache(slug: string, data: FeatureData): void {
  setCache(FEATURE_CACHE_PREFIX, slug, data, FEATURE_CACHE_EXPIRY);
}

function clearFeatureCache(slug: string): void {
  localStorage.removeItem(getCacheKey(FEATURE_CACHE_PREFIX, slug));
}

// Feature Credit Cost Cache Functions
export function getFeatureCreditCostCache(slug: string): number | null {
  return getCache(FEATURE_CREDIT_COST_CACHE_PREFIX, slug, FEATURE_CREDIT_COST_CACHE_EXPIRY);
}

export function setFeatureCreditCostCache(slug: string, creditCost: number): void {
  setCache(FEATURE_CREDIT_COST_CACHE_PREFIX, slug, creditCost, FEATURE_CREDIT_COST_CACHE_EXPIRY);
}

function clearFeatureCreditCostCache(slug: string): void {
  localStorage.removeItem(getCacheKey(FEATURE_CREDIT_COST_CACHE_PREFIX, slug));
}

// Favorites Cache Functions
export function getFavoritesCache(userId: string): FavoritesCache | null {
  return getCache(FAVORITES_CACHE_PREFIX, userId, FAVORITES_CACHE_EXPIRY);
}

export function setFavoritesCache(userId: string, data: FavoritesCache): void {
  setCache(FAVORITES_CACHE_PREFIX, userId, data, FAVORITES_CACHE_EXPIRY);
}

export function clearFavoritesCache(userId: string): void {
  localStorage.removeItem(getCacheKey(FAVORITES_CACHE_PREFIX, userId));
}

// My Drafts Cache Functions
export function getMyDraftsCache(userId: string): MyDraftsCache | null {
  return getCache(MY_DRAFTS_CACHE_PREFIX, userId, MY_DRAFTS_CACHE_EXPIRY);
}

export function setMyDraftsCache(userId: string, data: { files: any[] }): void {
  const cacheData: MyDraftsCache = {
    files: data.files,
    count: data.files.length,
    timestamp: Date.now()
  };
  setCache(MY_DRAFTS_CACHE_PREFIX, userId, cacheData, MY_DRAFTS_CACHE_EXPIRY);
}

export function clearMyDraftsCache(userId: string): void {
  localStorage.removeItem(getCacheKey(MY_DRAFTS_CACHE_PREFIX, userId));
}

// General Cache Management
function clearCache(prefix?: string): void {
  try {
    if (prefix) {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
    } else {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.error('Cache clear failed:', error);
  }
}

// Cache Health Check
function checkCacheHealth(): void {
  try {
    const testKey = 'cache_health_check';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
  } catch (error) {
    console.error('Cache health check failed:', error);
    clearCache();
  }
}

// Initialize cache health check
checkCacheHealth();